"use client";

import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

interface MapboxFeature {
  id: string;
  place_name: string;
  center: [number, number];
}

export type GeoSearchQuery = {
  query: string;
  lat: number;
  lng: number;
};

interface LocationSearchProps {
  accessToken: string;
  onLocationSelect?: (location: {
    name: string;
    lat: number;
    lng: number;
  }) => void;
  placeholder?: string;
  /** Syncs to the input (e.g. iframe default location query). */
  externalQuery?: string;
  /** Fetches suggestions for query, auto-selects the first, or searches with query + coords. */
  geoQuery?: GeoSearchQuery;
  iconPosition?: "start" | "end";
  shellClassName?: string;
  inputClassName?: string;
  iconClassName?: string;
  resultsListClassName?: string;
}

const defaultShellClass =
  "bg-white border border-black/10 rounded-xl px-4 py-3 flex gap-2 items-center transition-colors hover:border-black focus-within:border-black";

const defaultInputClass =
  "flex-1 outline-none text-base text-black/80 bg-transparent placeholder:text-black/50 min-w-0";

const defaultIconClass =
  "shrink-0 text-black/60 group-focus-within:text-black transition-colors";

const defaultResultsListClass =
  "absolute z-10 w-full mt-1 bg-white border border-black/10 rounded-xl shadow-lg max-h-60 overflow-auto";

export const SearchField: React.FC<LocationSearchProps> = ({
  accessToken,
  onLocationSelect,
  placeholder = "Enter zip code or city",
  externalQuery,
  geoQuery,
  iconPosition = "start",
  shellClassName,
  inputClassName,
  iconClassName,
  resultsListClassName,
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<MapboxFeature[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queryRef = useRef(query);
  queryRef.current = query;

  const fetchSuggestions = useCallback(
    async (searchQuery: string): Promise<MapboxFeature[]> => {
      const res = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          searchQuery,
        )}.json?access_token=${accessToken}&autocomplete=true&limit=5&country=US&types=postcode,place,region,country,neighborhood`,
      );
      const data = await res.json();
      return data.features || [];
    },
    [accessToken],
  );

  const applySelection = useCallback(
    (location: { name: string; lat: number; lng: number }) => {
      setIsOpen(false);
      setActiveIndex(-1);
      setQuery(location.name);
      onLocationSelect?.(location);
    },
    [onLocationSelect],
  );

  useEffect(() => {
    if (!externalQuery) {
      return;
    }

    setQuery(externalQuery);
    setIsOpen(true);
    setActiveIndex(-1);
  }, [externalQuery]);

  useEffect(() => {
    if (!geoQuery) {
      return;
    }

    let cancelled = false;
    const { query: searchQuery, lat, lng } = geoQuery;

    setIsOpen(true);
    setActiveIndex(-1);

    (async () => {
      try {
        const features = await fetchSuggestions(searchQuery);
        if (cancelled) {
          return;
        }
        setResults(features);

        if (features.length > 0 && features[0]) {
          applySelection({
            name: features[0].place_name,
            lng: features[0].center[0],
            lat: features[0].center[1],
          });

          return;
        }

        applySelection({ name: searchQuery, lat, lng });
      } catch (err) {
        console.error("Error fetching locations:", err);
        if (!cancelled) {
          setResults([]);
          applySelection({ name: searchQuery, lat, lng });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [geoQuery, fetchSuggestions, applySelection]);

  useEffect(() => {
    if (geoQuery || !query.trim()) {
      if (!query.trim()) {
        setResults([]);
      }
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    const fetchQuery = query;

    debounceRef.current = setTimeout(() => {
      (async () => {
        try {
          const features = await fetchSuggestions(fetchQuery);
          if (fetchQuery === queryRef.current) setResults(features);
        } catch (err) {
          console.error("Error fetching locations:", err);
          if (fetchQuery === queryRef.current) setResults([]);
        }
      })();
    }, 400);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, geoQuery, fetchSuggestions]);

  const handleSelect = (place: MapboxFeature) => {
    if (!place.center) {
      return;
    }
    applySelection({
      name: place.place_name,
      lng: place.center[0],
      lat: place.center[1],
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = activeIndex >= 0 ? results[activeIndex] : results[0];
      if (target) handleSelect(target);
    }
  };

  const shellClass = shellClassName ?? defaultShellClass;
  const inputClass = inputClassName ?? defaultInputClass;
  const iconClass = iconClassName ?? defaultIconClass;
  const resultsListClass = resultsListClassName ?? defaultResultsListClass;

  const searchIcon = (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={iconClass}
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );

  return (
    <div className="relative w-full">
      <div className={shellClass}>
        {iconPosition === "end" ? (
          <>
            <input
              type="text"
              className={inputClass}
              placeholder={placeholder}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setIsOpen(true);
              }}
              onKeyDown={handleKeyDown}
            />
            {searchIcon}
          </>
        ) : (
          <>
            {searchIcon}
            <input
              type="text"
              className={inputClass}
              placeholder={placeholder}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setIsOpen(true);
              }}
              onKeyDown={handleKeyDown}
            />
          </>
        )}
      </div>

      {isOpen && results.length > 0 && (
        <ul className={resultsListClass}>
          {results.map((place, index) => (
            <li
              key={place.id}
              onClick={() => handleSelect(place)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleSelect(place);
              }}
              className={`px-4 py-2 cursor-pointer text-sm text-[#0b1c24] hover:bg-gray-50 ${index === activeIndex ? "bg-gray-100" : ""}`}
            >
              {place.place_name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
